import { Injectable } from '@angular/core';
import { Apollo } from 'apollo-angular';
import { Observable } from 'rxjs';
import { Query_GetEquipmentCountingJournalMetrics } from '../../../../@core/graphql/queries/equipment-counting-journal/getEquipmentCountingJournalMetrics.query';
import { Query_GetEquipmentCountingJournalSlicerValues } from '../../../../@core/graphql/queries/equipment-counting-journal/getEquipmentCountingJournalSlicerValues.query';
import {
  Query_GetEquipmentCountingJournalCountsByStatus,
  Query_GetEquipmentCountingJournalLineCountsByStatus,
  Query_GetEquipmentCountingJournalCountsByLocation,
  Query_GetEquipmentCountingJournalTrends,
  Query_GetEquipmentCountingJournalLineCountsByDisposition,
} from '../../../../@core/graphql/queries/equipment-counting-journal/getEquipmentCountingJournalCountsByStatus.query';
import { Query_GetLostEquipmentDetails } from '../../../../@core/graphql/queries/equipment-counting-journal/getLostEquipmentDetails.query';
import { Query_GetNotFoundEquipmentDetails } from '../../../../@core/graphql/queries/equipment-counting-journal/getNotFoundEquipmentDetails.query';

export interface EquipmentCountingJournalMetricsFilter {
  locationNum?: string[];
  journalId?: string[];
  journalType?: string[];
  fleetType?: string[];
  equipmentCategory?: string[];
  journalStatus?: string[];
  createdPeriod?: string;
  startDate?: string;
  endDate?: string;
  excludeJournalStatus?: string[];
}

export interface EquipmentCountingJournalMetrics {
  totalJournals: number;
  totalLines: number;
  completedJournals: number;
  inProgressJournals: number;
  readyJournals: number;
  recountJournals: number;
  cancelledJournals: number;
  countedLines: number;
  waitingCountLines: number;
  notFoundLines: number;
  addedLines: number;
  otherLocationLines: number;
  completionRate: number;
  varianceRate: number;
  // New accuracy metrics
  accuracyRate: number;
  incorrectLocationRate: number;
  incorrectLocationCount: number;
  incorrectStationRate: number;
  incorrectStationCount: number;
  // Missing equipment metrics
  missingRate: number;
  missingCount: number;
  missingBookValue: number;
  estimatedReplacementCost: number;
  missingAvgAge: number;
}

export interface SlicerOption {
  value: string;
  label?: string;
  count?: number;
}

export interface EquipmentCountingJournalSlicerValues {
  statuses: SlicerOption[];
  locations: SlicerOption[];
  journalIds: SlicerOption[];
  journalTypes: SlicerOption[];
  fleetTypes: SlicerOption[];
  equipmentCategories: SlicerOption[];
  createdPeriods: SlicerOption[];
}

export interface StatusCount {
  status: string;
  count: number;
}

export interface LocationCount {
  locationNum: string;
  locationName: string;
  count: number;
}

export interface TrendData {
  date: string;
  totalJournals: number;
  completedJournals: number;
  totalLines: number;
  countedLines: number;
}

export interface DispositionCount {
  disposition: string;
  count: number;
}

export interface LostEquipmentCategoryDetail {
  category: string;
  count: number;
  bookValue: number;
  replacementCost: number;
}

export interface LostEquipmentLocationDetail {
  locationNum: string;
  locationName: string;
  totalCount: number;
  totalBookValue: number;
  totalReplacementCost: number;
  categories: LostEquipmentCategoryDetail[];
}

export interface LostEquipmentDetails {
  totalCount: number;
  totalBookValue: number;
  totalReplacementCost: number;
  avgAge: number;
  byLocation: LostEquipmentLocationDetail[];
  byCategory: LostEquipmentCategoryDetail[];
}

@Injectable({
  providedIn: 'root',
})
export class EquipmentCountingMetricsService {
  constructor(private apollo: Apollo) {}

  getMetrics(filter: EquipmentCountingJournalMetricsFilter): Observable<any> {
    return this.apollo.query({
      query: Query_GetEquipmentCountingJournalMetrics,
      variables: {
        filter: filter,
      },
      fetchPolicy: 'network-only',
    });
  }

  getSlicerValues(
    filter?: EquipmentCountingJournalMetricsFilter,
  ): Observable<any> {
    return this.apollo.query({
      query: Query_GetEquipmentCountingJournalSlicerValues,
      variables: {
        filter: filter || {},
      },
      fetchPolicy: 'network-only',
    });
  }

  getJournalCountsByStatus(
    filter: EquipmentCountingJournalMetricsFilter,
  ): Observable<any> {
    return this.apollo.query({
      query: Query_GetEquipmentCountingJournalCountsByStatus,
      variables: {
        filter: filter,
      },
      fetchPolicy: 'network-only',
    });
  }

  getLineCountsByStatus(
    filter: EquipmentCountingJournalMetricsFilter,
  ): Observable<any> {
    return this.apollo.query({
      query: Query_GetEquipmentCountingJournalLineCountsByStatus,
      variables: {
        filter: filter,
      },
      fetchPolicy: 'network-only',
    });
  }

  getJournalCountsByLocation(
    filter: EquipmentCountingJournalMetricsFilter,
  ): Observable<any> {
    return this.apollo.query({
      query: Query_GetEquipmentCountingJournalCountsByLocation,
      variables: {
        filter: filter,
      },
      fetchPolicy: 'network-only',
    });
  }

  getTrends(
    filter: EquipmentCountingJournalMetricsFilter,
    groupBy = 'day',
  ): Observable<any> {
    return this.apollo.query({
      query: Query_GetEquipmentCountingJournalTrends,
      variables: {
        filter: filter,
        groupBy: groupBy,
      },
      fetchPolicy: 'network-only',
    });
  }

  getLineCountsByDisposition(
    filter: EquipmentCountingJournalMetricsFilter,
  ): Observable<any> {
    return this.apollo.query({
      query: Query_GetEquipmentCountingJournalLineCountsByDisposition,
      variables: {
        filter: filter,
      },
      fetchPolicy: 'network-only',
    });
  }

  getLostEquipmentDetails(
    filter: EquipmentCountingJournalMetricsFilter,
  ): Observable<any> {
    return this.apollo.query({
      query: Query_GetLostEquipmentDetails,
      variables: {
        filter: filter,
      },
      fetchPolicy: 'network-only',
    });
  }

  getNotFoundEquipmentDetails(
    filter: EquipmentCountingJournalMetricsFilter,
  ): Observable<any> {
    return this.apollo.query({
      query: Query_GetNotFoundEquipmentDetails,
      variables: {
        filter: filter,
      },
      fetchPolicy: 'network-only',
    });
  }
}
